import {
    DailyVideo,
    useDaily,
    useDailyEvent,
    useDevices,
    useLocalSessionId,
    useVideoTrack,
} from '@daily-co/daily-react';
import { useCallback, useEffect, useState } from 'react';
import { getRoomDataFromSlug } from '../../config/roomMapping';
import { useSessionUser } from '../../context/SessionUserContext';
import { useWSMessage } from '../../context/WSMessageContext';
import { extractFirstName } from '../../utils/nameFormatter';
import UserMediaError from '../UserMediaError/UserMediaError';

import './HairCheck.css';

export default function HairCheck({ joinCall }) {
  const localSessionId = useLocalSessionId();
  const { currentCam, currentMic, currentSpeaker, microphones, speakers, cameras, setMicrophone, setCamera, setSpeaker } = useDevices();
  const callObject = useDaily();
  const { joinRoom, roomId } = useWSMessage();
  const { capture } = useSessionUser();
  const videoState = useVideoTrack(localSessionId);

  const [getUserMediaError, setGetUserMediaError] = useState(false);
  const [userName, setUserName] = useState(null);
  const [firstName, setFirstName] = useState('');

  // Busca o nome do participante do Supabase
  useEffect(() => {
    const fetchUserName = async () => {
      try {
        // Extrai o slug da URL
        const { pathname } = window.location;
        const slug = pathname.replace(/^\/+|\/+$/g, '');

        if (!slug) {
          return;
        }

        // Busca os dados da sala (sempre vem só um participante)
        const roomData = await getRoomDataFromSlug(slug);
        if (roomData && roomData.participants && roomData.participants.length > 0) {
          const participant = roomData.participants[0];
          const fullName = participant.user_name;

          if (fullName) {
            setUserName(fullName);
            const { firstName: first } = extractFirstName(fullName);
            setFirstName(first);

            // Define o nome no Daily também
            if (callObject) {
              callObject.setUserName(fullName);
            }
          }
        }
      } catch (err) {
        // Erro ao buscar nome do participante - ignora silenciosamente
      }
    };

    fetchUserName();
  }, [callObject]);

  useDailyEvent(
    'camera-error',
    useCallback(() => {
      setGetUserMediaError(true);
    }, []),
  );

  const handleJoin = (e) => {
    e.preventDefault();

    // Primeiro faz join-room no WebSocket se tiver roomId
    if (roomId && joinRoom) {
      joinRoom(roomId);
    }

    // Salva o nome antes do Daily montar (sobrevive ao unmount do DailyProvider)
    capture(userName);
    // Depois entra na call do Daily (usa o nome completo do Supabase)
    joinCall(userName || '');
  };

  const updateMicrophone = (e) => {
    setMicrophone(e.target.value);
  };

  const updateSpeakers = (e) => {
    setSpeaker(e.target.value);
  };

  const updateCamera = (e) => {
    setCamera(e.target.value);
  };

  return getUserMediaError ? (
    <UserMediaError />
  ) : (
    <div className="hair-check-container">
    <img src="/images/palmeira.png" alt="" className="hair-check-palmeira" />
    <img src="/images/folha2_vermelha.png" alt="" className="hair-check-folha2_vermelha" />
    <h1 className="hair-check-title">
      {firstName ? `Welcome, ${firstName}!` : 'Welcome! Ready to get started?'}
    </h1>
    <h2 className="hair-check-subtitle">Configure your camera and microphone before joining</h2>
    <div className="hair-check-content">
    <form className="hair-check" onSubmit={handleJoin}>
      <h2>Setup your hardware</h2>
      {/* Video preview */}

      {/* Microphone select */}
      <div>
        <label htmlFor="micOptions">Microphone:</label>
        <select name="micOptions" id="micSelect" onChange={updateMicrophone} value={currentMic?.device?.deviceId}>
          {microphones.map((mic) => (
            <option key={`mic-${mic.device.deviceId}`} value={mic.device.deviceId}>
              {mic.device.label}
            </option>
          ))}
        </select>
      </div>

      {/* Speakers select */}
      <div>
        <label htmlFor="speakersOptions">Speakers:</label>
        <select name="speakersOptions" id="speakersSelect" onChange={updateSpeakers} value={currentSpeaker?.device?.deviceId}>
          {speakers.map((speaker) => (
            <option key={`speaker-${speaker.device.deviceId}`} value={speaker.device.deviceId}>
              {speaker.device.label}
            </option>
          ))}
        </select>
      </div>

      {/* Camera select */}
      <div>
        <label htmlFor="cameraOptions">Camera:</label>
        <select name="cameraOptions" id="cameraSelect" onChange={updateCamera} value={currentCam?.device?.deviceId}>
          {cameras.map((camera) => (
            <option key={`cam-${camera.device.deviceId}`} value={camera.device.deviceId}>
              {camera.device.label}
            </option>
          ))}
        </select>
      </div>

      <button onClick={handleJoin} type="submit" className="join-call-button">
        Join call
      </button>
    </form>

    <div className="hair-check-video-container">
      <div className="hair-check-video">
        {localSessionId && <DailyVideo sessionId={localSessionId} mirror />}
        {videoState.isOff && (
          <svg
            className="hair-check-camera-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M23 7l-7 5 7 5V7z"
              fill="white"
            />
            <path
              d="M15 5H3c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2z"
              fill="white"
            />
          </svg>
        )}
      </div>
      {firstName && firstName.trim().length > 0 && (
        <div className="hair-check-username">
          {firstName}
        </div>
      )}
    </div>
    </div>
    <img src="/images/logo_contorno.png" alt="Logo" className="hair-check-logo" />
    </div>
  );
}
